import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Card, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Popover, Skeleton, Tooltip, Typography,} from '@mui/material';
import {AddOutlined, BookmarkBorderOutlined, BookmarkOutlined, CloudSyncRounded, DeleteOutline, EditOutlined, SettingsOutlined,} from '@mui/icons-material';
import Button from '../../../../../../../../components/base/button/button';
import Dialog from '../../../../../../../../components/base/dialog';
import useIsMounted from '../../../../../../../../hooks/use-is-mounted';
import {createUUId} from '../../../../../../../../../core/services/utils/utils';
import ValidateMessages from "../../../../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form, {MuiRFF, Yup} from "../../../../../../../../components/form";
import {isFormula} from "../../../../../../../../../core/services/formula-engine";
import Api from "../../../../../../../../../core/services/api/api";
import LoadingLottieAnimation from "../../../../../../../../components/app-specific/lottie-animations/loading";
import useCachedPriceLevelsDictionaryController from "../../../../../../../../hooks/use-price-level-dictionary";

/**
 * Renders the template management bar shown at the top of the live price column.
 *
 * Features:
 * - Clickable card showing the selected template name (opens popover list on click)
 * - "Save as Template" button — captures current formula state
 * - "Manage Templates" icon button — opens full management dialog
 *
 * @param {{
 *   formulaList: Array<{priceLevelId: number, formula: string}>,
 *   selectedTemplateId: string | null,
 *   onTemplateSelected: (template: object) => Promise<void>,
 *   loading: boolean,
 *   disabled: boolean,
 *   onApplyingTemplateAsCurrentFormulaState: (isApplying: boolean) => void,
 * }} props
 */
const FormulaTemplateBar = ({
                                formulaList = [],
                                onTemplateSelected,
                                loading = false,
                                disabled = false,
                                onApplyingTemplateAsCurrentFormulaState,
                            }) => {
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [syncingTemplateWithCurrentAppliedFormula, setSyncingTemplateWithCurrentAppliedFormula] = useState(false);
    const [applyingTemplateAsCurrentFormula, setApplyingTemplateAsCurrentFormula] = useState(null);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [saving, setSaving] = useState(false);
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const [upsertDialog, setUpsertDialog] = useState({open: false, templateToEdit: null});
    const [manageDialog, setManageDialog] = useState(false);
    const isMounted = useIsMounted();
    const popoverId = useRef(createUUId());

    const [fetchingItems, setFetchingItems] = useState({});
    const fetchingItemsPromises = useRef({});


    const selectedTemplate = useMemo(() => templates.find(t => t.id === selectedTemplateId) ?? null, [templates, selectedTemplateId]);

    const hasActiveFormulas = useMemo(() => {
        return formulaList.some(f => isFormula(f.formula));
    }, [formulaList]);

    const formulaListWithOnlyFormulas = useMemo(() => {
        return formulaList.filter(f => isFormula(f.formula));
    }, [formulaList]);

    /**
     * Fetches the list of saved formula templates from the server and updates local state.
     * @type {(function(): Promise<void>)|*}
     */
    const fetchTemplates = useCallback(async () => {
        setLoadingTemplates(true);
        const response = await Api.listPriceFormulaTemplates();
        if (!isMounted()) return;
        setLoadingTemplates(false);
        if (response.resultFlag) {
            setTemplates(response.data ?? []);
        }
    }, [isMounted]);

    /**
     * Initial fetch of templates on mount. Also refetches whenever the parent signals a change in the formula list, to ensure template data is up-to-date.
     */
    useEffect(() => {
        void fetchTemplates();
    }, [fetchTemplates]);

    /**
     * Utility function to check if a template has its items (price levels) loaded. Used to determine whether we need to fetch the items before showing them in the tooltip.
     * @type {function(*): boolean}
     */
    const doesTemplateHaveItems = useCallback((template) => {
        return !!template.items;
    }, []);

    /**
     * Fetches the price levels (items) for a given template if they haven't been loaded yet.
     * Updates local state with the fetched items to enable display in the tooltip.
     * @type {(function(*): Promise<*>)}
     */
    const fetchItems = React.useCallback(async (_template) => {
        const template = {..._template};
        if (doesTemplateHaveItems(template)) {
            return template;
        }
        if (fetchingItemsPromises.current[template.id]) {
            return await fetchingItemsPromises.current[template.id];
        }
        const promise = new Promise(async (resolve) => {
            setFetchingItems(prev => ({...prev, [template.id]: true}));
            const response = await Api.getPriceFormulaTemplate(template.id);
            if (!isMounted()) return;
            setFetchingItems((prev) => ({...prev, [template.id]: false}));
            if (response.resultFlag) {
                template.items = response.data?.items ?? [];
                setTemplates(prev => prev.map(t => t.id === template.id ? template : t));
                resolve(template);
            } else {
                resolve(null);
            }
        });
        fetchingItemsPromises.current[template.id] = promise;
        return await promise;
    }, [doesTemplateHaveItems, isMounted]);

    /**
     * Handles the selection of a template from the popover list.
     * @type {(function(*): void)}
     */
    const handleTemplateSelection = useCallback(async (template) => {
        setApplyingTemplateAsCurrentFormula(template.id);
        onApplyingTemplateAsCurrentFormulaState?.(true);
        if (onTemplateSelected) {
            const newTemplate = await fetchItems(template);
            if (!isMounted())
                return;
            if (!!newTemplate) {
                const confirmed = await onTemplateSelected(newTemplate);
                if (!isMounted())
                    return;
                if (confirmed) {
                    setSelectedTemplateId(newTemplate.id);
                }
            }
        }
        setApplyingTemplateAsCurrentFormula(null);
        onApplyingTemplateAsCurrentFormulaState?.(false);
        setPopoverAnchor(null);
    }, [fetchItems, onTemplateSelected, isMounted]);

    /**
     * Handles saving the current formula list as a new template with the given name. Calls the API to create the template, updates local state, and notifies parent of the new template.
     * @type {(function(*): Promise<void>)}
     */
    const handleSaveAsTemplate = useCallback(async (name) => {
        setSaving(true);
        const response = await Api.createPriceFormulaTemplate({name});
        if (!isMounted()) return;
        if (!response?.resultFlag)
            return;
        const newTemplate = response.data;
        const assignmentResponse = await Api.setPriceFormulaTemplateItems({templateId: newTemplate.id, items: formulaListWithOnlyFormulas})
        if (!isMounted()) return;
        if (!assignmentResponse?.resultFlag)
            return;
        newTemplate.items = formulaListWithOnlyFormulas;
        setTemplates(prev => [...prev, newTemplate]);
        setSaving(false);
        void handleTemplateSelection(newTemplate);
    }, [formulaListWithOnlyFormulas, handleTemplateSelection, isMounted]);

    /**
     * Handles setting the current formulas to match a selected template. Calls the API to update the template's items, and if successful, notifies the parent of the updated template so it can update the UI accordingly.
     * @type {(function(*): Promise<void>)|*}
     */
    const handleSyncTemplateWithCurrentAplliedFormula = useCallback(async (template) => {
        setSyncingTemplateWithCurrentAppliedFormula(true);
        const response = await Api.setPriceFormulaTemplateItems({templateId: template.id, items: formulaListWithOnlyFormulas});
        if (!isMounted()) return;
        setSyncingTemplateWithCurrentAppliedFormula(false);
        if (response.resultFlag) {
            const templateWithItems = {...template, items: formulaListWithOnlyFormulas};
            setTemplates(prev => prev.map(t => t.id === template.id ? templateWithItems : t));
        }
    }, [formulaListWithOnlyFormulas, isMounted]);

    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap'}}>
                {/* Template selector card */}
                <Card
                    sx={{
                        flexGrow: 1,
                        px: 2,
                        py: 0.75,
                        cursor: disabled ? 'default' : 'pointer',
                        backgroundColor: 'background.default',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 0,
                    }}
                    onClick={disabled ? undefined : (e) => setPopoverAnchor(e.currentTarget)}
                >
                    <BookmarkOutlined sx={{color: 'text.secondary', flexShrink: 0}}/>
                    <Typography
                        variant="body2"
                        color={selectedTemplate ? 'text.primary' : 'text.secondary'}
                        noWrap
                        sx={{flexGrow: 1, minWidth: 0}}
                    >
                        {
                            loadingTemplates
                                ? 'Loading formulas…'
                                : selectedTemplate
                                    ? `Formula Template: ${selectedTemplate.name}`
                                    : 'Select formula template'
                        }
                    </Typography>
                    {
                        hasActiveFormulas &&
                        <Tooltip title="Set Current Formulas to this Template">
                            <Box component={'span'}>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={disabled || loading}
                                    onClick={() => handleSyncTemplateWithCurrentAplliedFormula(selectedTemplate)}
                                >
                                    {syncingTemplateWithCurrentAppliedFormula ? <CircularProgress size={16} color="primary"/> : <CloudSyncRounded/>}
                                </IconButton>
                            </Box>
                        </Tooltip>
                    }
                    <Tooltip title="Manage formula templates">
                        <Box component={'span'}>
                            <IconButton
                                size="small"
                                disabled={disabled || loading}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPopoverAnchor(null);
                                    setManageDialog(true);
                                }}
                            >
                                <SettingsOutlined fontSize="small"/>
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Card>
                {
                    !!applyingTemplateAsCurrentFormula &&
                    <Typography component={'span'} variant="caption" sx={{mx: 2, fontWeight: 700, color: 'tertiary.main', fontStyle: 'italic', mt: -0.75}}>
                        Applying Formula Template: {templates.find(t => t.id === applyingTemplateAsCurrentFormula)?.name ?? ''}...
                    </Typography>
                }
                {/* Save as template */}
                <Tooltip title={saving ? 'Saving…' : !hasActiveFormulas ? 'At least one dynamic formula is required to create a template' : 'Save current formulas as a template'}>
                    <Box component={'span'} sx={{width: '100%'}}>
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={disabled || saving || loading || !hasActiveFormulas}
                            onClick={() => setUpsertDialog({open: true, templateToEdit: null})}
                            sx={{whiteSpace: 'nowrap', flexShrink: 0, width: '100%'}}
                        >
                            {saving ? <CircularProgress size={14}/> : <AddOutlined sx={{fontSize: 16, mr: 0.5}}/>}
                            Save as Formula Template
                        </Button>
                    </Box>
                </Tooltip>
            </Box>
            {/* Template selection popover */}
            <Popover
                id={popoverAnchor ? popoverId.current : undefined}
                open={!!popoverAnchor}
                anchorEl={popoverAnchor}
                onClose={() => setPopoverAnchor(null)}
                anchorReference={'anchorEl'}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                transformOrigin={{vertical: 'top', horizontal: 'center'}}
                slotProps={{
                    paper: {
                        sx: {minWidth: 400,}
                    },
                }}
            >
                <Paper sx={{py: 1, maxHeight: 320, overflowY: 'auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 1}}>
                    <Typography variant="body1" color="text.primary" sx={{px: 2, pb: 1, display: 'block'}}>
                        Available Formula Templates
                    </Typography>
                    <Divider sx={{mb: 1}}/>
                    {
                        loadingTemplates &&
                        Array.from({length: 5}).map((_, i) => (
                            <Skeleton key={i} height={44} sx={{mx: 1, transform: 'none'}}/>
                        ))
                    }
                    {
                        !loadingTemplates && !templates.length &&
                        <Typography variant="body2" color="text.secondary" sx={{px: 2, py: 6, textAlign: 'center'}}>
                            No templates saved yet.
                        </Typography>
                    }
                    {
                        !loadingTemplates &&
                        templates.map(template => (
                            <Box
                                key={template.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1,
                                    cursor: 'pointer',
                                    gap: 1,
                                    borderRadius: 1,
                                    mx: 0.5,
                                    backgroundColor: template.id === selectedTemplateId ? 'action.selected' : 'transparent',
                                    '&:hover': {backgroundColor: 'action.hover'},
                                }}
                                onClick={!applyingTemplateAsCurrentFormula ? () => handleTemplateSelection(template) : void 0}
                            >
                                {
                                    fetchingItems[template.id] || applyingTemplateAsCurrentFormula === template.id
                                        ? <CircularProgress size={16} color="primary"/>
                                        : template.id === selectedTemplateId
                                            ? <BookmarkOutlined sx={{color: 'primary.main'}}/>
                                            : <BookmarkBorderOutlined sx={{color: 'text.disabled'}}/>
                                }
                                <Typography variant="body1" fontWeight={template.id === selectedTemplateId ? 600 : 400}>
                                    {template.name} {applyingTemplateAsCurrentFormula === template.id && '(Applying…)'}
                                </Typography>
                                <PriceLevelsTooltip
                                    template={template}
                                    fetchingItems={fetchingItems}
                                    doesTemplateHaveItems={doesTemplateHaveItems}
                                    fetchItems={fetchItems}
                                />
                                {
                                    hasActiveFormulas &&
                                    <Tooltip title="Set Current Formulas to this Template">
                                        <Box component={'span'}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                disabled={disabled || loading}
                                                onClick={() => handleSyncTemplateWithCurrentAplliedFormula(template)}
                                            >
                                                {syncingTemplateWithCurrentAppliedFormula ? <CircularProgress size={16} color="primary"/> : <CloudSyncRounded/>}
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                }
                            </Box>
                        ))
                    }
                </Paper>
            </Popover>
            <UpsertFormulaTemplateDialog
                open={upsertDialog.open}
                templateToEdit={upsertDialog.templateToEdit}
                onClose={() => setUpsertDialog({open: false, templateToEdit: null})}
                onSave={handleSaveAsTemplate}
            />
            <ManageFormulaTemplatesDialog
                open={manageDialog}
                onClose={() => setManageDialog(false)}
                templates={templates}
                setTemplates={setTemplates}
                selectedTemplateId={selectedTemplateId}
                setSelectedTemplateId={setSelectedTemplateId}
                fetchingItems={fetchingItems}
                doesTemplateHaveItems={doesTemplateHaveItems}
                fetchItems={fetchItems}
            />
        </>
    );
};

export default FormulaTemplateBar;


const PriceLevelsTooltip = ({template, fetchingItems: _fetchingItems, doesTemplateHaveItems, fetchItems}) => {
    const {priceLevelDict} = useCachedPriceLevelsDictionaryController({loadDict: true});
    const [open, setOpen] = useState(false);
    const hasItems = useMemo(() => doesTemplateHaveItems(template), [template, doesTemplateHaveItems]);
    const fetchingItems = _fetchingItems?.[template.id];

    /**
     * Triggers fetching of price levels when the tooltip is opened, but only if they haven't been fetched already.
     * This ensures that we have the necessary data to display in the tooltip without preloading it unnecessarily.
     */
    useEffect(() => {
        if (open) {
            void fetchItems?.(template);
        }
    }, [fetchItems, open, template])

    return (
        <>
            <Tooltip
                onClose={() => setOpen(false)}
                onOpen={() => setOpen(true)}
                open={open}
                slotProps={{
                    tooltip: {
                        ...(!fetchingItems && hasItems && {
                            sx: {maxWidth: 500, minWidth: 'min(450px, 90vw)', p: 0},
                        })
                    },
                }}
                title={
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        {
                            fetchingItems &&
                            <LoadingLottieAnimation width={'50%'}/>
                        }
                        {
                            !fetchingItems && hasItems &&
                            <Box sx={{maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, padding: 2}}>
                                <Typography variant='body2' fontWeight={500}>
                                    {template.items?.length ?? 0} price levels in this template have dynamic formulas:
                                </Typography>
                                <FormulaPriceLevelsTable
                                    items={template.items}
                                    priceLevelDict={priceLevelDict}
                                />
                            </Box>
                        }
                        {
                            !fetchingItems && !hasItems &&
                            "Has not yet loaded price levels"
                        }
                    </Box>
                }
            >
                <Typography variant="body2" color="text.primary" sx={{ml: 'auto'}}>
                    {
                        hasItems &&
                        `${template.items?.length ?? 0} price levels`
                    }
                    {
                        !hasItems && !fetchingItems &&
                        "View affected price levels"
                    }
                    {
                        !hasItems && fetchingItems &&
                        "Loading price levels..."
                    }
                </Typography>
            </Tooltip>
        </>
    );
}

export const FormulaPriceLevelsTable = ({items, priceLevelDict, bolded = false}) => {

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, paddingInline: 1}}>
            {
                items?.map((pl) => (
                    <Box key={pl.priceLevelId} sx={{display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.5, px: 1}}>
                        <Typography variant="caption" sx={{py: 0.2, borderRadius: 0.5, ...(bolded && {fontWeight: 600})}}>
                            PL {pl.priceLevelId} - {priceLevelDict?.find(p => p.priceLevelId === pl.priceLevelId)?.title ?? pl.priceLevelTitle ?? ""}
                        </Typography>
                        <Typography variant="caption" sx={{fontWeight: 500, whiteSpace: 'pre-wrap', ...(bolded && {fontWeight: 600})}}>
                            {pl.formula}
                        </Typography>
                    </Box>
                ))
            }
        </Box>
    );
}


// ─── ManageFormulaTemplatesDialog ─────────────────────────────────────────────

const ManageFormulaTemplatesDialog = ({
                                          open,
                                          onClose,
                                          templates,
                                          setTemplates,
                                          selectedTemplateId,
                                          setSelectedTemplateId,
                                          fetchingItems,
                                          doesTemplateHaveItems,
                                          fetchItems,
                                      }) => {
    const [removingIds, setRemovingIds] = useState([]);
    const [updatingIds, setUpdatingIds] = useState([]);
    const [upsertState, setUpsertState] = useState({open: false, templateToEdit: null});
    const isMounted = useIsMounted();

    /**
     * Removes a formula template by calling the delete API, updating local state, and handling edge cases like if the deleted template is currently selected.
     * @type {(function(*): Promise<void>)|*}
     */
    const removeTemplate = useCallback(async (template) => {
        setRemovingIds(prev => [...prev, template.id]);
        const response = await Api.deletePriceFormulaTemplate(template.id);
        if (!isMounted()) return;
        setRemovingIds(prev => prev.filter(id => id !== template.id));
        if (response.resultFlag) {
            setTemplates(prev => prev.filter(t => t.id !== template.id));
            setSelectedTemplateId(prev => prev === template.id ? null : prev);
        }
    }, [isMounted, setTemplates, setSelectedTemplateId]);

    /**
     * Handles renaming an existing template by calling the update API, updating local state, and ensuring the UI reflects the change. Also manages loading state for the update operation.
     * @type {(function(*, *): Promise<void>)|*}
     */
    const handleUpsert = useCallback(async (name, templateToEdit) => {
        if (templateToEdit) {
            setUpdatingIds(prev => [...prev, templateToEdit.id]);
            const response = await Api.updatePriceFormulaTemplate({
                id: templateToEdit.id,
                name: name,
            });
            if (!isMounted()) return;
            setUpdatingIds(prev => prev.filter(id => id !== templateToEdit.id));
            if (response.resultFlag) {
                setTemplates(prev => prev.map(t => t.id === templateToEdit.id ? {...t, name} : t));
            }
        }
    }, [isMounted, setTemplates]);

    return (
        <>
            <Dialog open={open} setOpen={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Manage Formula Templates
                </DialogTitle>
                <DialogContent dividers>
                    {
                        !templates.length &&
                        <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 8}}>
                            No formula templates saved yet.
                        </Typography>
                    }
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                        {
                            templates.map(template => {
                                const removing = removingIds.includes(template.id);
                                const updating = updatingIds.includes(template.id);
                                return (
                                    <Card
                                        key={template.id}
                                        variant="outlined"
                                        sx={{
                                            px: 2,
                                            py: 1.25,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            borderColor: template.id === selectedTemplateId ? 'primary.main' : undefined,
                                            backgroundColor: template.id === selectedTemplateId ? 'primary.light' : undefined,
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={600} sx={{flexGrow: 1}}>
                                            {template.name}
                                        </Typography>
                                        <PriceLevelsTooltip
                                            template={template}
                                            fetchingItems={fetchingItems}
                                            doesTemplateHaveItems={doesTemplateHaveItems}
                                            fetchItems={fetchItems}
                                        />
                                        <Tooltip title="Rename">
                                            <Box component={'span'}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    disabled={removing || updating}
                                                    onClick={() => setUpsertState({open: true, templateToEdit: template})}
                                                >
                                                    <EditOutlined fontSize="small"/>
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                        <Tooltip title={removing ? 'Removing…' : 'Delete template'}>
                                            <Box component={'span'}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    disabled={removing || updating}
                                                    onClick={() => removeTemplate(template)}
                                                >
                                                    {removing
                                                        ? <CircularProgress size={16} color="error"/>
                                                        : <DeleteOutline fontSize="small"/>
                                                    }
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                    </Card>
                                );
                            })
                        }
                    </Box>
                </DialogContent>
                <DialogActions sx={{p: 0}}>
                    <Button
                        variant="dialog"
                        color="primary"
                        onClick={onClose}
                        borderRadius="8px 0 8px 0"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Rename dialog */}
            <UpsertFormulaTemplateDialog
                open={upsertState.open}
                templateToEdit={upsertState.templateToEdit}
                onClose={() => setUpsertState({open: false, templateToEdit: null})}
                onSave={(name) => handleUpsert(name, upsertState.templateToEdit)}
            />
        </>
    );
};

// ─── UpsertFormulaTemplateDialog ──────────────────────────────────────────────

const UpsertFormulaTemplateDialog = ({open, onClose, onSave, templateToEdit}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const schema = useMemo(() => Yup.object().shape({
        name: Yup.string().nullable().required(ValidateMessages.required)
    }), []);

    const validate = useMemo(() => makeValidate(schema), [schema]);
    const required = useMemo(() => makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        name: templateToEdit?.name ?? '',
    }), [templateToEdit]);

    /**
     * Handles form submission for both creating a new template and renaming an existing one.
     * Validates input, calls the provided onSave callback, and manages loading state.
     * @param e
     * @returns {Promise<void>}
     */
    const handleSubmit = useCallback(async (values) => {
        setSubmitting(true);
        await onSave(values.name);
        if (!isMounted()) return;
        setSubmitting(false);
        onClose();
    }, [onSave, onClose, isMounted]);

    return (
        <Dialog open={open} setOpen={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {templateToEdit ? 'Rename Formula Template' : 'Save as Formula Template'}
            </DialogTitle>
            <DialogContent dividers>
                <Form onSubmit={handleSubmit} validate={validate} initialValues={initialValues} formProps={{id: 'upsert-formula-template-form'}}>
                    <MuiRFF.TextField
                        name={"name"}
                        required={required.name}
                        label="Template Name"
                        disabled={submitting}
                        placeholder="e.g. Standard Markup"
                        autoFocus
                        fullWidth
                    />
                </Form>
            </DialogContent>
            <DialogActions sx={{p: 0, display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <Button
                    variant="dialog"
                    color="primary"
                    disabled={submitting}
                    onClick={onClose}
                    borderRadius="0 8px 0 8px"
                >
                    Cancel
                </Button>
                <Button
                    variant="dialog"
                    color="primary"
                    disabled={submitting}
                    form="upsert-formula-template-form"
                    type="submit"
                    borderRadius="8px 0 8px 0"
                >
                    {submitting
                        ? (templateToEdit ? 'Saving…' : 'Creating…')
                        : (templateToEdit ? 'Save Changes' : 'Create Template')
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
};
